import { Eye, MousePointerClick, PencilLine, Send, Download, Heart } from 'lucide-react';
import type { EngagementRow, EngagementStatus } from '../services/magnetPortalService';

const CHIPS: { key: keyof EngagementStatus; label: string; icon: any }[] = [
  { key: 'opened',     label: 'Opened',     icon: Eye },
  { key: 'clicked',    label: 'Clicked',    icon: MousePointerClick },
  { key: 'filled',     label: 'Filled',     icon: PencilLine },
  { key: 'submitted',  label: 'Submitted',  icon: Send },
  { key: 'downloaded', label: 'Downloaded', icon: Download },
  { key: 'liked',      label: 'Liked',      icon: Heart },
];

const CONTACT_STATUS: Record<string, string> = {
  COLD:    'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  ENGAGED: 'bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400',
  ACTIVE:  'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
};

function StatusChips({ status }: { status: EngagementStatus }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {CHIPS.map(({ key, label, icon: Icon }) => {
        const on = status[key];
        return (
          <span
            key={key}
            title={label}
            className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium border transition-colors ${
              on
                ? 'bg-primary/10 text-primary border-primary/20'
                : 'bg-muted/40 text-muted-foreground/50 border-transparent'
            }`}
          >
            <Icon className="h-3 w-3" />
            {label}
          </span>
        );
      })}
    </div>
  );
}

function timeAgo(iso: string | null) {
  if (!iso) return '—';
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return 'just now';
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

export default function EngagementTable({
  rows, loading, onSelect,
}: {
  rows: EngagementRow[];
  loading: boolean;
  onSelect: (row: EngagementRow) => void;
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }
  if (!rows.length) {
    return (
      <div className="text-center py-16 text-sm text-muted-foreground">
        No engagement yet. Once contacts interact with magnets on resources.cantier.com, it appears here.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-muted-foreground border-b border-border">
            <th className="py-2.5 px-3 font-medium">Contact</th>
            <th className="py-2.5 px-3 font-medium">Magnet</th>
            <th className="py-2.5 px-3 font-medium">Engagement</th>
            <th className="py-2.5 px-3 font-medium">Score</th>
            <th className="py-2.5 px-3 font-medium">Last activity</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.deliveryId}
              onClick={() => onSelect(r)}
              className="border-b border-border/60 hover:bg-muted/40 cursor-pointer transition-colors"
            >
              <td className="py-2.5 px-3">
                <div className="font-medium text-foreground">
                  {r.contact.firstName} {r.contact.lastName}
                </div>
                <div className="text-xs text-muted-foreground">
                  {r.contact.email || '—'}{r.contact.account ? ` · ${r.contact.account.name}` : ''}
                </div>
              </td>
              <td className="py-2.5 px-3">
                <div className="text-foreground">{r.magnet.title}</div>
                <div className="text-xs text-muted-foreground">{r.magnet.magnetType || r.magnet.contentType || ''}</div>
              </td>
              <td className="py-2.5 px-3"><StatusChips status={r.status} /></td>
              <td className="py-2.5 px-3">
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${CONTACT_STATUS[r.contact.status] || CONTACT_STATUS.COLD}`}>
                  {r.contact.engagementScore} · {r.contact.status}
                </span>
              </td>
              <td className="py-2.5 px-3 text-xs text-muted-foreground whitespace-nowrap">
                {timeAgo(r.timestamps?.lastEventAt)} · {r.eventCount} events
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
